import {
  ArrowSquareOut,
  Check,
  CheckCircle,
  CircleNotch,
  FigmaLogo,
  FileDoc,
  Folder,
  Plug,
  SlackLogo,
  Warning,
} from "@phosphor-icons/react";
import {
  Badge,
  Box,
  Dialog,
  Flex,
  Heading,
  Text,
  VisuallyHidden,
} from "@radix-ui/themes";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import SlackViewer from "./SlackViewer";

import { useIntegrationHealth } from "../../utils/queries/enterprises";

/**
 * Vitalora demo integrations overview (S4 of the build brief).
 *
 * Six cards in two rows of three:
 *   - Wrike  : real OAuth state (now reads MG's Composio entity).
 *   - Asana  : real OAuth state (same).
 *   - Slack  : synthetic — opens the in-app Slack viewer (C5).
 *   - Figma  : synthetic — opens a screenshot dialog.
 *   - GoogleDocs : synthetic — opens a screenshot dialog.
 *   - SharePoint : synthetic — shows the deck's mandated result counts (8/22/5/4).
 *
 * The build brief is explicit that "all 6 tiles show green/active. No
 * error states anywhere." Synthetic cards always render green so the
 * demoer never has to apologise for a yellow chip.
 */
type PreviewCard = "slack" | "figma" | "gdocs" | "sharepoint";

const PREVIEW_DIALOG_TITLES: Record<PreviewCard, string> = {
  slack: "Slack",
  figma: "Figma",
  gdocs: "Google Docs",
  sharepoint: "SharePoint",
};

export default function VitaloraIntegrations() {
  const { t } = useTranslation("ui");
  const health = useIntegrationHealth();
  const [openCard, setOpenCard] = useState<null | PreviewCard>(null);
  const previewCardTitle = openCard
    ? PREVIEW_DIALOG_TITLES[openCard]
    : undefined;

  const wrikeOk = health.data?.wrike?.connected === true;
  const asanaOk = health.data?.asana?.connected === true;

  return (
    <Box className="mx-auto w-full max-w-[1100px] px-6 py-8">
      <Heading size="6">{t("vitaloraIntegrations.view.integrations")}</Heading>
      <Text size="2" color="gray">
        Puntt joins your existing tools like a contractor — no platform
        migration required. Six integrations are live for Vitalora.
      </Text>

      <Flex
        wrap="wrap"
        gap="3"
        mt="6"
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
      >
        <IntegrationCard
          name="Wrike"
          icon={Plug}
          status={health.isPending ? "pending" : wrikeOk ? "active" : "warning"}
          description={t("vitaloraIntegrations.view.punttAiJoinedAsContractor")}
          meta={
            wrikeOk
              ? `Account ${health.data?.wrike?.accountId?.slice(0, 8)} · ${
                  health.data?.wrike?.invocationCount ?? 0
                } invocations`
              : "Pre-prod connection"
          }
        />
        <IntegrationCard
          name="Asana"
          icon={Plug}
          status={health.isPending ? "pending" : asanaOk ? "active" : "warning"}
          description={t(
            "vitaloraIntegrations.view.oauthConnectedPunttAssignedReview",
          )}
          meta={
            asanaOk
              ? `Account ${health.data?.asana?.accountId?.slice(0, 8)} · ${
                  health.data?.asana?.webhookCount ?? 0
                }/${health.data?.asana?.totalWebhooks ?? 0} webhooks`
              : "Pre-prod connection"
          }
        />
        <IntegrationCard
          data-testid="vitalora-integration-slack"
          name="Slack"
          icon={SlackLogo}
          status="active"
          description={t(
            "vitaloraIntegrations.view.botInstalledVitaloraReviewsChannel",
          )}
          meta="#vitalora-reviews · 10 messages today"
          onClick={() => setOpenCard("slack")}
        />
        <IntegrationCard
          data-testid="vitalora-integration-figma"
          name="Figma"
          icon={FigmaLogo}
          status="active"
          description={t(
            "vitaloraIntegrations.view.apiConnectedLuminosOohCampaign",
          )}
          meta="2 of 3 artboards annotated"
          onClick={() => setOpenCard("figma")}
        />
        <IntegrationCard
          data-testid="vitalora-integration-gdocs"
          name="Google Docs"
          icon={FileDoc}
          status="active"
          description={t(
            "vitaloraIntegrations.view.oauthConnectedVitacoreBrief3",
          )}
          meta="2 documents · 4 comments"
          onClick={() => setOpenCard("gdocs")}
        />
        <IntegrationCard
          data-testid="vitalora-integration-sharepoint"
          name="SharePoint"
          icon={Folder}
          status="readonly"
          description={t(
            "vitaloraIntegrations.view.readOnlyAccessBrandGuidelines",
          )}
          meta="Result counts 8 / 22 / 5 / 4"
          onClick={() => setOpenCard("sharepoint")}
        />
      </Flex>

      <Dialog.Root
        open={openCard === "slack"}
        onOpenChange={(o) => !o && setOpenCard(null)}
      >
        <Dialog.Content size="3" maxWidth="640px">
          <VisuallyHidden>
            <Dialog.Title as="h2">{PREVIEW_DIALOG_TITLES.slack}</Dialog.Title>
          </VisuallyHidden>
          <SlackViewer />
        </Dialog.Content>
      </Dialog.Root>

      <Dialog.Root
        open={
          openCard === "figma" ||
          openCard === "gdocs" ||
          openCard === "sharepoint"
        }
        onOpenChange={(o) => !o && setOpenCard(null)}
      >
        <Dialog.Content size="3" maxWidth="540px">
          <VisuallyHidden>
            <Dialog.Title as="h2">{previewCardTitle}</Dialog.Title>
          </VisuallyHidden>
          {openCard === "figma" ? <FigmaPreview /> : null}
          {openCard === "gdocs" ? <GoogleDocsPreview /> : null}
          {openCard === "sharepoint" ? <SharePointPreview /> : null}
        </Dialog.Content>
      </Dialog.Root>
    </Box>
  );
}

interface IntegrationCardProps {
  name: string;
  icon: typeof SlackLogo;
  status: "active" | "readonly" | "warning" | "pending";
  description: string;
  meta?: string;
  onClick?: () => void;
  "data-testid"?: string;
}

function IntegrationCard({
  name,
  icon: Icon,
  status,
  description,
  meta,
  onClick,
  "data-testid": testId,
}: IntegrationCardProps) {
  const Content = (
    <Flex direction="column" gap="2" className="h-full">
      <Flex align="center" justify="between">
        <Flex align="center" gap="2">
          <Icon size={22} weight="duotone" className="text-puntt-accent-9" />
          <Heading size="3">{name}</Heading>
        </Flex>
        <StatusBadge status={status} />
      </Flex>
      <Text size="2" color="gray">
        {description}
      </Text>
      <Flex justify="between" align="center" mt="auto" pt="2">
        {meta ? (
          <Text size="1" color="gray">
            {meta}
          </Text>
        ) : (
          <span />
        )}
        {onClick ? (
          <ArrowSquareOut size={16} className="text-puntt-accent-11" />
        ) : null}
      </Flex>
    </Flex>
  );

  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId ?? "integration-card-button"}
      className="rounded-xl border border-puntt-neutral-gray-6 bg-base-white p-4 text-left shadow-sm transition hover:border-puntt-accent-7 hover:shadow-md"
    >
      {Content}
    </button>
  ) : (
    <div className="rounded-xl border border-puntt-neutral-gray-6 bg-base-white p-4 shadow-sm">
      {Content}
    </div>
  );
}

function StatusBadge({ status }: { status: IntegrationCardProps["status"] }) {
  if (status === "pending") {
    return (
      <Badge color="gray" variant="soft">
        <CircleNotch size={12} className="animate-spin" /> Pending
      </Badge>
    );
  }
  if (status === "warning") {
    return (
      <Badge color="amber" variant="soft">
        <Warning size={12} /> Reconnect
      </Badge>
    );
  }
  if (status === "readonly") {
    return (
      <Badge color="grass" variant="soft">
        <CheckCircle size={12} /> Read-only
      </Badge>
    );
  }
  return (
    <Badge color="grass" variant="soft">
      <Check size={12} /> Active
    </Badge>
  );
}

function FigmaPreview() {
  const { t } = useTranslation("ui");
  return (
    <Flex direction="column" gap="2">
      <Heading size="4">
        {t("vitaloraIntegrations.view.figmaLuminosOohCampaign")}
      </Heading>
      <Text size="2" color="gray">
        {t("vitaloraIntegrations.view.3Artboards2ActivePuntt")}
      </Text>
      <div className="font-mono mt-2 rounded-lg border border-puntt-neutral-gray-6 bg-puntt-neutral-gray-2 p-4 text-sm">
        Artboard 1 — Hero Print
        <br />
        &nbsp;&nbsp;⚑ Brand: logo placement outside §3.2 safe zone ( Vitalora
        Brand Bible)
        <br />
        &nbsp;&nbsp;⚑ Copy: SPF claim missing localised UK phrasing
        <br />
        Artboard 2 — Bus Shelter
        <br />
        &nbsp;&nbsp;⚑ Brand: secondary mark — confirmed approved
        <br />
        Artboard 3 — Outdoor 6-sheet
        <br />
        &nbsp;&nbsp;✓ All checks passed
      </div>
    </Flex>
  );
}

function GoogleDocsPreview() {
  const { t } = useTranslation("ui");
  return (
    <Flex direction="column" gap="2">
      <Heading size="4">
        {t("vitaloraIntegrations.view.googleDocsLinkedDocuments")}
      </Heading>
      <Text size="2" color="gray">
        {t("vitaloraIntegrations.view.2ConnectedDocuments4Native")}
      </Text>
      <div className="mt-2 rounded-lg border border-puntt-neutral-gray-6 bg-puntt-neutral-gray-2 p-4 text-sm">
        <strong>
          {t("vitaloraIntegrations.view.vitacoreBriefUsCampaignQ2")}
        </strong>
        <br />
        &nbsp;&nbsp;3 Puntt comments on brief completeness.
        <br />
        <strong>
          {t("vitaloraIntegrations.view.luminosClaimDermatologistTested")}
        </strong>
        <br />
        &nbsp;&nbsp;1 Puntt HIGH RISK flag on claim language.
      </div>
    </Flex>
  );
}

function SharePointPreview() {
  const { t } = useTranslation("ui");
  return (
    <Flex direction="column" gap="2">
      <Heading size="4">
        {t("vitaloraIntegrations.view.sharepointIndexedSources")}
      </Heading>
      <Text size="2" color="gray">
        Read-only access. Result counts match the deck screenshots — these
        numbers appear during the Claims Intelligence demo.
      </Text>
      <div className="mt-2 rounded-lg border border-puntt-neutral-gray-6 bg-puntt-neutral-gray-2 p-4 text-sm">
        /Brand-Guidelines · 8 results
        <br />
        /Approved-Claims · 22 results
        <br />
        /Artwork · 5 results
        <br />
        /Claims-DB · 4 results
      </div>
    </Flex>
  );
}
